/** Ruler tick-spacing math for Tier 2 Group D rulers / grid /
 *  guides. Pure functions — no DOM. Picks scene-space tick
 *  intervals that translate to roughly the same screen density
 *  across all zoom levels, snapping to a 1-2-5-10 ladder so the
 *  numeric labels stay legible (10, 20, 50, 100, 200, 500 …).
 *
 *  Shared between `CanvasOverlays/Rulers` (tick marks + labels)
 *  and `CanvasOverlays/Grid` (line spacing matches user-set
 *  `gridConfig.size` directly, not this helper — the helper is
 *  for the ruler/cursor coordinate scale). */

/** Target screen-pixel spacing between major ticks. ~80px gives
 *  4-7 major ticks across a typical viewport width — enough
 *  detail for orientation without crowding the labels. */
const TARGET_MAJOR_SCREEN_PX = 80;

export interface TickSpacing {
  /** Distance between MAJOR ticks in scene-space pixels. Labels
   *  render here. Always a member of the 1-2-5-10 ladder. */
  major: number;
  /** Distance between MINOR ticks in scene-space pixels. Always
   *  `major / 5` so minor ticks divide evenly between labeled
   *  positions. */
  minor: number;
}

/** Pick the scene-space tick interval for a given zoom level.
 *  Snaps to a 1-2-5-10 ladder so labels are always round
 *  numbers. Returns `{ major: 1, minor: 0.2 }` at the small-end
 *  limit (very high zoom showing sub-pixel detail). */
export function pickTickSpacing(zoom: number): TickSpacing {
  if (!Number.isFinite(zoom) || zoom <= 0) return { major: 100, minor: 20 };
  const targetScene = TARGET_MAJOR_SCREEN_PX / zoom;
  // Decompose targetScene as `normalized * 10^magnitude` where
  // 1 ≤ normalized < 10. Then snap `normalized` to the
  // 1-2-5-10 ladder by rounding to the nearest entry — picking
  // the round number CLOSEST to the target rather than only
  // snapping down. With target 8 (zoom 1), this yields major=100
  // rather than major=50, matching Photoshop's ruler density. */
  const magnitude = Math.pow(10, Math.floor(Math.log10(targetScene)));
  const normalized = targetScene / magnitude;
  let majorMul: number;
  if (normalized < 1.5) majorMul = 1;
  else if (normalized < 3.5) majorMul = 2;
  else if (normalized < 7.5) majorMul = 5;
  else majorMul = 10;
  const major = majorMul * magnitude;
  return { major, minor: major / 5 };
}

/** Compute the visible scene-space coordinate range on one axis,
 *  given the viewport size, current pan offset, and zoom. The
 *  returned range is half-open [min, max). Used by ruler tick
 *  renderers to know which scene coords are on screen. */
export function visibleSceneRange(
  panPx: number,
  viewportPx: number,
  zoom: number,
): { min: number; max: number } {
  if (zoom <= 0) return { min: 0, max: 0 };
  // `|| 0` coerces -0 to +0 so callers using strict equality
  // (Object.is / toEqual) match the expected sign.
  return {
    min: -panPx / zoom || 0,
    max: (viewportPx - panPx) / zoom || 0,
  };
}

/** Yield every tick position in scene-space coordinates between
 *  `min` and `max`, aligned to `spacing`. Returns a fresh array
 *  each call — callers iterate, don't mutate. */
export function tickPositions(min: number, max: number, spacing: number): number[] {
  if (spacing <= 0 || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [];
  const start = Math.ceil(min / spacing) * spacing;
  const out: number[] = [];
  for (let v = start; v < max; v += spacing) {
    // Round to avoid float-accumulation drift at very small
    // spacings (the user wouldn't notice a 0.0001px shift but
    // the label "9.99999" would). `|| 0` coerces -0 to +0.
    out.push(Math.round(v * 1e6) / 1e6 || 0);
  }
  return out;
}
